'use client';

import { useMemo } from 'react';
import { useDeviceTypeContext } from '@/app/utils/getDeviceType';
import { FeaturePost } from './components/featurePost';
import { MostRecentBlock } from './components/mostRecentBlock';
import ContentletFilter from '@/components/shared/contentletFilter';
import { extractPostSections } from './utils';
import { CollectionCard } from './components/collectionCard';
import {
    buildContentTypeQueryConfig,
    calculateLimit
} from '@/app/utils/content-type-query-builder';

/**
 * This function returns a string with the inodes of the contentlets
 *
 * @param {*} [contentlets=[]]
 * @return {*}
 */
const excludedInodes = (contentlets = []) => {
    const inodes = contentlets?.map((content) => content?.inode);
    return `${inodes.join(' || ')}`;
};

export function Collection({
    hideFilter,
    hideFilterWithContent,
    hideSearchBar,
    showCategoryFromSlug,
    featurePost,
    mostRecentBlock,
    contentVariable,
    widgetCodeJSON,
    showContributorFilter,
    showTagsFilter,
    filterContributorByCompany,
    centerPositionCategoryFilter,
    showAuthor,
    autoPriority,
    conhostId,
    content
}) {
    const deviceType = useDeviceTypeContext();
    const { total, posts, categories, query, sortBy } = widgetCodeJSON;

    const hasPosts = Array.isArray(posts) && posts.length > 0;

    const showFeaturePost = featurePost?.toLowerCase() === 'true';
    const showMostRecentBlock = mostRecentBlock?.toLowerCase() === 'true';

    const { main, mostRecent, listing } = hasPosts
        ? extractPostSections({
              showFeaturePost,
              showMostRecentBlock,
              posts
          })
        : { main: null, mostRecent: [], listing: [] };

    const inodesExcluded = excludedInodes([main, ...mostRecent]);
    const initialCollection = {
        contentlets: [...listing],
        total
    };

    const queryConfig = useMemo(() => {
        const contentTypeQueryConfig = buildContentTypeQueryConfig(contentVariable, conhostId);

        const queryWithExclusions = `${contentTypeQueryConfig.query} ${
            inodesExcluded ? `-inode:(${inodesExcluded})` : ''
        }`;

        return {
            sortBy: sortBy || contentTypeQueryConfig.sortBy,
            defaultQuery: queryWithExclusions,
            searchQuery: query || contentTypeQueryConfig.query
        };
    }, [sortBy, query, inodesExcluded, contentVariable, conhostId]);

    return (
        <div className="container m-auto flex w-full flex-col">
            {!!main && <FeaturePost contentlet={main} />}
            {!!mostRecent.length && (
                <MostRecentBlock posts={mostRecent} contenttype={contentVariable} />
            )}
            <div className="flex flex-col gap-6">
                {!hideFilterWithContent && (
                    <ContentletFilter
                        Card={({ contentlet, ...props }) => (
                            <CollectionCard
                                showAuthor={showAuthor}
                                contentlet={contentlet}
                                contenttype={contentVariable}
                                {...props}
                            />
                        )}
                        showContributorFilter={!!showContributorFilter}
                        showTagsFilter={!!showTagsFilter}
                        filterContributorByCompany={filterContributorByCompany}
                        hideFilter={hideFilter}
                        hideSearchBar={hideSearchBar}
                        showCategoryFromSlug={showCategoryFromSlug}
                        showAuthor={showAuthor}
                        centerPositionCategoryFilter={centerPositionCategoryFilter}
                        limit={calculateLimit(showFeaturePost)}
                        queryConfig={queryConfig}
                        categories={categories}
                        contentType={contentVariable}
                        initialCollection={initialCollection}
                        conhostId={conhostId}
                        autoPriority={autoPriority}
                        deviceType={deviceType}
                        contributors={
                            content.contributors?.sort((a, b) =>
                                a.firstName.localeCompare(b.firstName)
                            ) || []
                        }
                    />
                )}
            </div>
        </div>
    );
}
